import React, { useRef, useEffect, useState } from "react";
import ReactMapGL from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import * as MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import { Row, Col } from "react-bootstrap";
import { Table, Button, Tooltip, Spin, Switch } from "antd";
import { Icon } from "semantic-ui-react";
import { Marker, Popup, FlyToInterpolator, Layer, Source } from "react-map-gl";
import data from "../constants/menu";
import { ConsoleSqlOutlined } from "@ant-design/icons";
/* eslint import/no-webpack-loader-syntax: off */
import mapboxgl from "!mapbox-gl";

mapboxgl.accessToken =
  "pk.eyJ1IjoiYmFjay1vZmZpY2UtbWFuYWdlbWVudCIsImEiOiJjbGZxcjcyZ2wwMTk1M3hraTBmYndycnhnIn0.nCdXVyBW6n3Db_vdDcE3gA";
export default function LocationMap(props) {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [mapData, setMapData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mapStyle, setMapStyle] = useState("mapbox://styles/mapbox/light-v10");
  const nextMarkerId = useRef(0);

  const getRandomColor = () =>
    "#" + Math.floor(Math.random() * 16777215).toString(16);

  const copyToClipboard = (latitude, longitude) => {
    const textToCopy = `${latitude}, ${longitude}`;
    const textField = document.createElement("textarea");
    textField.innerText = textToCopy;
    document.body.appendChild(textField);
    textField.select();
    document.execCommand("copy");
    textField.remove();
    alert("Latitude and Longitude copied to clipboard!");
  };

  useEffect(() => {
    if (map.current) return;
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: mapStyle,
      center: [props.long || 114.1838, props.lat || 22.2797],
      zoom: props.zoom || 5.5,
    });

    map.current.addControl(
      new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        mapboxgl: mapboxgl,
      })
    );
  }, [props.lat, props.long, props.zoom]);

  // Update map style dynamically
  useEffect(() => {
    if (map.current) {
      map.current.setStyle(mapStyle);
    }
  }, [mapStyle]);
  useEffect(() => {
    const handleResize = () => {
      if (map.current) {
        map.current.resize();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!map.current) return;

    map.current.on("click", async (e) => {
      const id = nextMarkerId.current++;
      const color = getRandomColor();
      setLoading(true);

      const marker = new mapboxgl.Marker({ color })
        .setLngLat([e.lngLat.lng, e.lngLat.lat])
        .addTo(map.current);

      setMapData((prevMapData) => [
        ...prevMapData,
        {
          id,
          marker,
          color,
          name: null,
          latitude: e.lngLat.lat,
          longitude: e.lngLat.lng,
        },
      ]);

      if (props.onGeocodingComplete) {
        props.onGeocodingComplete(false);
      }

      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${e.lngLat.lng},${e.lngLat.lat}.json?access_token=${mapboxgl.accessToken}`
        );
        const data = await response.json();
        const firstFeature = data.features[0];

        let locationName = null;

        if (firstFeature && firstFeature.context) {
          const country = firstFeature.context.find((c) =>
            c.id.startsWith("place")
          );
          const countryCode = country?.short_code?.toUpperCase();

          if (countryCode === "HK") {
            const osmResponse = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${e.lngLat.lat}&lon=${e.lngLat.lng}&zoom=18&addressdetails=1&accept-language=en`
            );
            const osmData = await osmResponse.json();
            locationName = osmData.name || osmData.display_name;
          } else {
            locationName = firstFeature.place_name.split(",")[0];
          }
        }

        setMapData((prevMapData) => {
          const updated = prevMapData.map((item) =>
            item.id === id ? { ...item, name: locationName } : item
          );

          const allNamed = updated.every((item) => item.name !== null);
          if (props.onGeocodingComplete) {
            props.onGeocodingComplete(allNamed);
          }

          return updated;
        });
      } catch (err) {
        console.error("Reverse geocoding failed:", err);
      } finally {
        setLoading(false);
      }
    });
  }, []);

  useEffect(() => {
    const allNamed = mapData.length > 0 && mapData.every((item) => item.name);
    if (allNamed) {
      const cleanedData = mapData.map(
        ({ name, latitude, longitude, color }) => ({
          name,
          latitude,
          longitude,
          color,
        })
      );
      props.onMapDataChange(cleanedData);
    }
  }, [mapData]);

  const handleDeleteMapData = (id) => {
    const toDelete = mapData.find((item) => item.id === id);
    if (toDelete) {
      toDelete.marker.remove();
      const updated = mapData.filter((item) => item.id !== id);
      setMapData(updated);
      if (props.onGeocodingComplete) {
        const isComplete =
          updated.length > 0 && updated.every((i) => i.name !== null);
        props.onGeocodingComplete(isComplete);
      }
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <span>
          <Icon name="map marker alternate" style={{ color: record.color }} />
          &nbsp;{text ? text.slice(0, 20) : "Loading..."}
        </span>
      ),
    },
    {
      title: "Latitude",
      dataIndex: "latitude",
      key: "latitude",
      render: (lat) => lat.toFixed(3),
    },
    {
      title: "Longitude",
      dataIndex: "longitude",
      key: "longitude",
      render: (lng) => lng.toFixed(3),
    },
    {
      title: "Delete",
      key: "delete",
      render: (_, record) => (
        <Tooltip title="Delete marker">
          <Icon
            name="trash alternate"
            onClick={() => handleDeleteMapData(record.id)}
            style={{ cursor: "pointer", color: "black" }}
          />
        </Tooltip>
      ),
    },
    {
      title: "Copy",
      key: "copy",
      render: (_, record) => (
        <Tooltip title="Copy coordinates">
          <Icon
            name="copy"
            onClick={() => copyToClipboard(record.latitude, record.longitude)}
            style={{ cursor: "pointer", color: "black" }}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div style={{ position: "relative" }}>
      {loading && (
        <div
          style={{ position: "absolute", top: 100, left: "45%", zIndex: 1000 }}
        >
          <Spin tip="Fetching location..." size="large" />
        </div>
      )}

      <div
        style={{
          position: "absolute",
          top: 10,
          left: 10,
          zIndex: 999,
          background: "white",
          padding: "5px 12px",
          borderRadius: "4px",
          fontSize: "14px",
        }}
      >
        <span style={{ marginRight: 8 }}>View:</span>
        <Switch
          checked={mapStyle.includes("satellite")}
          onChange={(checked) =>
            setMapStyle(
              checked
                ? "mapbox://styles/mapbox/satellite-streets-v11"
                : "mapbox://styles/mapbox/light-v10"
            )
          }
          checkedChildren="Satellite"
          unCheckedChildren="Street"
        />
      </div>

      <Row>
        <Col md={7} sm={12} xs={12} className="mapcol">
          <div ref={mapContainer} className="location-map-container" />
        </Col>
        <Col lg={5} className="asset">
          <Table
            className="dark-mode-table"
            columns={columns}
            dataSource={mapData.map((item) => ({ ...item, key: item.id }))}
            pagination={false}
          />
        </Col>
      </Row>
    </div>
  );
}
